import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatoRango } from './shared/horas.ts';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface DatosKiosk {
  cliente_id: string;
  cliente_nombre: string;
  salones: { id: string; nombre: string; radio_metros: number }[];
  qr: { cliente_id: string; min: number; hmac: string };
  valido_hasta: number;
  convocatorias: {
    convocatoria_id: string;
    persona_nombre: string;
    persona_apellido: string;
    categoria_nombre: string;
    salon_nombre: string;
    hora_inicio: string;
    hora_fin: string;
  }[];
  checkins: {
    persona_nombre: string;
    persona_apellido: string;
    salon_nombre: string;
    checkin_hora: string;
  }[];
}

function formatearHora(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' });
}

export function KioskPage() {
  const params = new URLSearchParams(window.location.search);
  const clienteId = params.get('cliente_id');
  const salonId = params.get('salon_id'); // legacy
  const pinCorrecto = params.get('pin');

  // Si se pasa salon_id legacy, redirigir a cliente_id
  const clienteIdFinal = clienteId || salonId;

  const [autenticado] = useState(true); // PIN viene en la URL
  const [datos, setDatos] = useState<DatosKiosk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(60);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);

  async function togglePantallaCompleta() {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setPantallaCompleta(true);
    } else {
      await document.exitFullscreen();
      setPantallaCompleta(false);
    }
  }

  const cargarDatos = useCallback(async () => {
    if (!clienteIdFinal) return;
    try {
      // Usar endpoint público con PIN en query string (sin JWT)
      const resp = await fetch(`${API_BASE}/api/cliente/${clienteIdFinal}/qr-publico?pin=${encodeURIComponent(pinCorrecto || '')}`);
      if (!resp.ok) throw new Error('Error al cargar datos del cliente');
      const data: DatosKiosk = await resp.json();
      setDatos(data);
      setError(null);
      setSegundosRestantes(60);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  }, [clienteIdFinal, pinCorrecto]);

  useEffect(() => {
    if (!autenticado) return;
    cargarDatos();
    const intervalo = setInterval(cargarDatos, 30000);
    return () => clearInterval(intervalo);
  }, [autenticado, cargarDatos]);

  useEffect(() => {
    if (!autenticado) return;
    const intervalo = setInterval(() => {
      setSegundosRestantes((s) => (s <= 1 ? 60 : s - 1));
    }, 1000);
    return () => clearInterval(intervalo);
  }, [autenticado]);

  if (!clienteIdFinal) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <p className="text-white text-xl">Falta el parámetro cliente_id</p>
      </div>
    );
  }

  if (error && !datos) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <p className="text-red-400 text-xl">{error}</p>
      </div>
    );
  }

  const qrData = datos ? JSON.stringify(datos.qr) : '';
  const porcentajeTiempo = datos ? ((60 - segundosRestantes) / 60) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{datos?.cliente_nombre || 'Club'}</h1>
          <p className="text-gray-400 text-sm">Código QR de check-in</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-400">Válido por: <span className="text-white font-mono text-lg">{segundosRestantes}s</span></p>
          <div className="w-32 h-2 bg-gray-700 rounded-full mt-1 overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-1000"
              style={{ width: `${porcentajeTiempo}%` }}
            />
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* QR */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          {datos?.qr && (
            <div className={`bg-white p-6 rounded-3xl shadow-2xl ${segundosRestantes <= 10 ? 'animate-pulse ring-4 ring-red-500' : ''}`}>
              <QRCodeSVG
                value={qrData}
                size={320}
                level="M"
                includeMargin={false}
              />
            </div>
          )}
          <p className="mt-6 text-gray-400 text-sm">
            Escaneá con la app de CONVOCA
          </p>
          {datos?.salones && datos.salones.length > 0 && (
            <p className="mt-1 text-gray-500 text-xs">
              Salones: {datos.salones.map(s => s.nombre).join(', ')}
            </p>
          )}
        </div>

        {/* Sidebar: check-ins y convocatorias */}
        <div className="w-full lg:w-96 bg-gray-800 p-6 overflow-y-auto">
          <h2 className="text-lg font-semibold mb-4">
            Check-ins de hoy ({datos?.checkins.length || 0})
          </h2>

          {datos?.checkins && datos.checkins.length > 0 ? (
            <ul className="space-y-2">
              {datos.checkins.map((c, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg bg-gray-700/50 px-4 py-3">
                  <div>
                    <p className="font-medium">{c.persona_apellido}, {c.persona_nombre}</p>
                    <p className="text-xs text-gray-400">{c.salon_nombre} · {formatearHora(c.checkin_hora)}</p>
                  </div>
                  <span className="text-green-400 text-xl">✓</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">Sin check-ins aún</p>
          )}

          {datos?.convocatorias && datos.convocatorias.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-400 mt-6 mb-2">
                Servicios programados ({datos.convocatorias.length})
              </h3>
              <ul className="space-y-1.5">
                {datos.convocatorias.map((c) => (
                  <li key={c.convocatoria_id} className="text-sm text-gray-300 rounded-lg bg-gray-700/30 px-3 py-2">
                    <span className="font-medium">{c.persona_apellido}, {c.persona_nombre}</span>
                    <span className="text-gray-500"> · {c.categoria_nombre}</span>
                    <span className="text-gray-500"> · {c.salon_nombre}</span>
                    <span className="text-gray-500"> · {formatoRango(c.hora_inicio, c.hora_fin)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 px-6 py-3 flex justify-end gap-4">
        <button
          className="text-sm text-gray-400 hover:text-white transition"
          onClick={togglePantallaCompleta}
        >
          {pantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'}
        </button>
        <button
          className="text-sm text-gray-400 hover:text-white transition"
          onClick={() => window.close()}
        >
          Cerrar kiosk
        </button>
      </footer>
    </div>
  );
}
